import { Button } from "@mantine/core";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ApiResponse } from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import type { WizardFormModeProps } from "../../../types/wizard.types";
import type { Person } from "../../../utils/backend-types";
import { applyHighlightingToObjectFormFieldData } from "../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { CreatableFormFieldController } from "../../BuildingBlocks/Forms/Controllers/CreatableFormFieldController";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import type {
  ConnectionCreateFormData,
  ConnectionResponse
} from "../Connection.types";
import type { MeteringConceptResponse } from "../hooks/useMeteringConcepts";
import { CONNECTION_FORM_FIELD_DATA } from "./ConnectionForm.constants";
import type { ConnectionFormData } from "./ConnectionForm.types";

type ConnectionFormProps = {
  isMieterstrom: boolean;
  meteringConcepts: Array<MeteringConceptResponse>;
  persons: Array<Person>;
  onCancel: () => void;
  onChangeNumHighlightedFields: (numHighlightedFields: number) => void;
  onSubmitCreate: (
    data: ConnectionCreateFormData
  ) => Promise<ApiResponse<ConnectionResponse>>;
  onSubmitUpdate: (
    data: ConnectionFormData,
    id: number
  ) => Promise<ApiResponse<ConnectionResponse>>;
} & WizardFormModeProps<ConnectionResponse>;

function ConnectionForm({
  isMieterstrom,
  meteringConcepts,
  persons,
  onCancel,
  onChangeNumHighlightedFields,
  onSubmitCreate,
  onSubmitUpdate,
  ...modeProps
}: ConnectionFormProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const { control, handleSubmit, reset, setError, setValue, watch } =
    useForm<ConnectionFormData>({
      defaultValues: modeProps.mode === "edit" ? modeProps.wizardObject : {}
    });
  const { formFieldData, numHighlightedFields } =
    applyHighlightingToObjectFormFieldData(
      CONNECTION_FORM_FIELD_DATA,
      watch,
      modeProps.mode === "edit" ? modeProps : undefined
    );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    onChangeNumHighlightedFields(numHighlightedFields);
  }, [numHighlightedFields, onChangeNumHighlightedFields]);

  const personChoices = persons.map((person) => ({
    value: person.id,
    label: person.name
  }));
  const meteringConceptChoices = meteringConcepts.map((meteringConcept) => ({
    value: meteringConcept.id,
    label: meteringConcept.name
  }));

  const onValidSubmit: SubmitHandler<ConnectionFormData> = async (data) => {
    setIsSubmitting(true);

    try {
      return await (modeProps.mode === "create"
        ? onSubmitCreate(data)
        : onSubmitUpdate(data, modeProps.wizardObject.id));
    } catch (error) {
      setIsSubmitting(false);
      setErrorsFromResponseData<ConnectionFormData>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  function handleClickCancel() {
    reset();
    onCancel();
  }

  return (
    <form className="ConnectionForm" onSubmit={submitCallback}>
      <FormFieldController control={control} data={formFieldData.name} />

      <FormFieldController control={control} data={formFieldData.voltage} />

      <FormFieldController
        control={control}
        data={formFieldData.grid_operator}
      />

      <FormFieldController
        control={control}
        data={{
          ...formFieldData.transmission_system_operator,
          required: !isMieterstrom
        }}
      />

      {isMieterstrom ? (
        <>
          <FormFieldController
            control={control}
            data={formFieldData.supply_type}
          />

          <FormFieldController
            control={control}
            data={{
              ...formFieldData.metering_concept,
              data: meteringConceptChoices
            }}
          />
        </>
      ) : (
        <>
          <CreatableFormFieldController
            control={control}
            data={{
              ...formFieldData.connecting_party,
              data: personChoices
            }}
            setValue={setValue}
            target="person"
          />

          <CreatableFormFieldController
            control={control}
            data={{
              ...formFieldData.supply_contract_held_by,
              data: personChoices
            }}
            setValue={setValue}
            target="person"
          />
        </>
      )}

      <ModalFooter>
        <Button
          disabled={isSubmitting}
          variant="default"
          onClick={handleClickCancel}
        >
          Abbrechen
        </Button>
        <Button
          color="secondary"
          loading={isSubmitting}
          onClick={submitCallback}
        >
          {modeProps.mode === "edit" ? "Speichern" : "Erstellen"}
        </Button>
      </ModalFooter>
    </form>
  );
}

export { ConnectionForm, ConnectionFormProps };
